import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import Footer from "../components/common/footer";
import NavBar from "../components/common/navBar";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/homepage.css";

const Homepage = () => {
	const [stayLogos, setStayLogos] = useState(false);
	const [logoSize, setLogoSize] = useState(120);
	const [isFixed, setIsFixed] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const handleScroll = useCallback(() => {
		requestAnimationFrame(() => {
			const scroll = Math.round(window.pageYOffset, 2);
			const minSize = 60;
			const maxSize = 120;
			const scrollThreshold = 100;
			
			const newSize = Math.max(minSize, maxSize - (scroll * 0.6));
			
			if (scroll >= scrollThreshold) {
				if (!isFixed) {
					setIsFixed(true);
					setStayLogos(true);
					setLogoSize(minSize);
				}
				return;
			} else {
				if (isFixed) {
					setIsFixed(false);
					setStayLogos(false);
				}
				setLogoSize(newSize);
			}
		});
	}, [isFixed]);

	useEffect(() => {
		const scrollHandler = () => {
			handleScroll();
		};

		window.addEventListener("scroll", scrollHandler, { passive: true });
		return () => window.removeEventListener("scroll", scrollHandler);
	}, [handleScroll]);

	const currentSEO = SEO.find((item) => item.page === "home");

	const logosStyle = {
		display: "flex",
		position: stayLogos ? "fixed" : "relative",
		top: stayLogos ? "3vh" : "auto",
		zIndex: 999,
		gap: "20px",
		alignItems: "center",
		transform: "translate3d(0, 0, 0)",
		willChange: stayLogos ? "auto" : "transform",
	};

	const headshotStyle = {
		borderRadius: "50%",
		border: stayLogos ? "1px solid white" : "none",
		boxShadow: stayLogos ? "0px 4px 10px rgba(0, 0, 0, 0.25)" : "none",
		width: logoSize,
		height: logoSize,
		objectFit: "cover",
		transform: "translate3d(0, 0, 0)",
		transition: isFixed ? "none" : "width 0.1s ease-out, height 0.1s ease-out",
		willChange: isFixed ? "auto" : "width, height",
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="home" />
				<div className="content-wrapper">
					<div className="homepage-logo-container">
						<div 
							style={logosStyle}
							className={stayLogos ? "homepage-logos-fixed" : ""}
						>
							<img 
								src="logan_headshot.png" 
								alt="Logan headshot" 
								style={headshotStyle}
							/>
							<img 
								src="henry_headshot.png" 
								alt="Henry headshot" 
								style={headshotStyle}
							/>
						</div>
					</div>

					<div className="homepage-container">
						<div className="homepage-first-area">
							<div className="homepage-first-area-left-side">
								<div className="title homepage-title">
									{INFO.homepage.title}
								</div>

								<div className="subtitle homepage-subtitle">
									{INFO.homepage.description} <strong style={{ color: 'var(--primary-color)' }}>delivering insights you can trust with impacts you can measure.</strong>
								</div>
							</div>

							<div className="homepage-first-area-right-side">
								<div className="homepage-image-container">
									<div className="homepage-image-wrapper">
										<img
											src="homepage.jpg"
											alt="about"
											className="homepage-image"
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="testimonial-section">
							<div className="testimonial-content">
								<div className="testimonial-quote">
									"Logan provided statistical support for a large clinical training project at our hospital. From start to finish, he was wonderful to work with on this project. Logan was easy to communicate with and was able to complete his statistical analysis following the project timeline. He was also able to effectively use data from prior statistics consultants and bring it all together to show our final projects results. I would highly recommend working with Logan."
								</div>
								<div className="testimonial-author">
									Lead Researcher - Psychiatric Hospital
								</div>
							</div>
						</div>

						<div className="navigation-links-section">
							<div className="nav-link-item">
								<div className="nav-link-text">Our Team</div>
								<Link to="/about" className="nav-link-circle">
									<FontAwesomeIcon icon={faArrowRight} className="nav-link-arrow" />
								</Link>
							</div>
							
							<div className="nav-link-item">
								<div className="nav-link-text">Offerings</div>
								<Link to="/projects" className="nav-link-circle">
									<FontAwesomeIcon icon={faArrowRight} className="nav-link-arrow" />
								</Link>
							</div>
							
							<div className="nav-link-item">
								<div className="nav-link-text">Why Us?</div>
								<Link to="/articles" className="nav-link-circle">
									<FontAwesomeIcon icon={faArrowRight} className="nav-link-arrow" />
								</Link>
							</div>
							
							<div className="nav-link-item">
								<div className="nav-link-text">Contact</div>
								<Link to="/contact" className="nav-link-circle">
									<FontAwesomeIcon icon={faArrowRight} className="nav-link-arrow" />
								</Link>
							</div>
						</div>

						<div className="page-footer">
							<Footer />
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Homepage;